// LuviaRx — AI email writer inside the admin Newsletter compose card.
import React, { useState } from "react";
import { Sparkles, Loader2, Wand2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IDEAS = [
  "Announce our current sale to all customers — warm and short",
  "Last-chance reminder: the sale ends this week",
  "Welcome back email for customers who haven't ordered in a while",
];

// Parses "SUBJECT: / CTA_LABEL: / CTA_URL: / BODY:" streamed text.
const parse = (text) => {
  const grab = (k) => (text.match(new RegExp(`^${k}:\\s*(.*)$`, "m")) || [])[1]?.trim() || "";
  const i = text.indexOf("BODY:");
  return {
    subject: grab("SUBJECT"),
    ctaLabel: grab("CTA_LABEL"),
    ctaUrl: grab("CTA_URL"),
    body: i >= 0 ? text.slice(i + 5).replace(/^\s*\n/, "") : "",
  };
};

export default function NewsletterAiWriter({ token, currentBody, onDraft }) {
  const [prompt, setPrompt] = useState("");
  const [includeSale, setIncludeSale] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const run = async (revise) => {
    if (prompt.trim().length < 3) { setError("Tell the AI what to write first."); return; }
    setBusy(true); setError("");
    let text = "";
    try {
      const res = await fetch(`${API}/admin/newsletter/ai-draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt, include_sale: includeSale, current_draft: revise ? currentBody : "" }),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const ev of events) {
          if (!ev.startsWith("data: ")) continue;
          const d = JSON.parse(ev.slice(6));
          if (d.error) throw new Error(d.error);
          if (d.delta) { text += d.delta; onDraft(parse(text)); }
        }
      }
    } catch (e) {
      setError(e.message || "AI writer failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="newsletter-ai-writer" className="rounded-2xl border border-[#8B2252]/15 bg-[#FDFAF9] p-4 space-y-3">
      <div className="flex items-center gap-2 text-[#8B2252]">
        <Sparkles className="w-4 h-4" />
        <span className="text-xs font-semibold uppercase tracking-[0.18em]">AI email writer</span>
      </div>
      <textarea
        data-testid="newsletter-ai-prompt-input"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={2}
        maxLength={2000}
        placeholder="Describe the email, e.g. “Announce the fall sale, mention it ends soon, friendly tone”"
        className="input resize-y"
      />
      <div className="flex flex-wrap gap-2">
        {IDEAS.map((idea, i) => (
          <button key={idea} type="button" onClick={() => setPrompt(idea)} data-testid={`newsletter-ai-idea-${i}`}
            className="text-[11px] px-2.5 py-1 rounded-full border border-charcoal-200 text-charcoal-500 hover:border-[#8B2252]/40 hover:text-[#8B2252] transition-colors">
            {idea}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" disabled={busy} onClick={() => run(false)} data-testid="newsletter-ai-generate-btn"
          className="btn-primary inline-flex items-center gap-1.5 !py-2 text-sm">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Write draft
        </button>
        {currentBody?.trim() && (
          <button type="button" disabled={busy} onClick={() => run(true)} data-testid="newsletter-ai-revise-btn"
            className="text-sm text-[#8B2252] hover:underline disabled:opacity-50">
            Rewrite my current draft
          </button>
        )}
        <label className="ml-auto flex items-center gap-2 text-xs text-charcoal-500">
          <input type="checkbox" checked={includeSale} onChange={(e) => setIncludeSale(e.target.checked)} data-testid="newsletter-ai-include-sale" />
          Include the live sale details
        </label>
      </div>
      {error && <p data-testid="newsletter-ai-error" className="text-xs text-red-600">{error}</p>}
      <p className="text-[11px] text-charcoal-400">The draft fills the subject, body and button below. Read it and edit anything before you send.</p>
    </div>
  );
}
